import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { MapPin, Building2, Home, Briefcase, X, Check, AlertCircle, List, Map as MapIcon } from 'lucide-react'
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { TeamMember, RoleWithMember, OrgChartService } from '../../services/orgChartService'
import { PersonCard } from './PersonCard'

// Fix for default marker icons in Leaflet with webpack/vite
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

interface LocationViewProps {
  teamMembers: TeamMember[]
  roles: RoleWithMember[]
  isAdmin: boolean
  onUpdateLocation: () => void
}

// Predefined office locations (can be customized)
const OFFICE_LOCATIONS = [
  { label: 'Auckland Office', coordinates: { lat: -36.8509, lng: 174.7645 } },
  { label: 'Wellington Office', coordinates: { lat: -41.2866, lng: 174.7756 } },
  { label: 'Christchurch Office', coordinates: { lat: -43.5321, lng: 172.6362 } },
  { label: 'Remote - Auckland', coordinates: { lat: -36.8485, lng: 174.7633 } },
  { label: 'Remote - Wellington', coordinates: { lat: -41.2924, lng: 174.7787 } },
  { label: 'Remote - Christchurch', coordinates: { lat: -43.5350, lng: 172.6397 } }
]

// Custom marker icons by location type
const createCustomIcon = (type: 'office' | 'remote' | 'hybrid' | null, photoUrl?: string | null) => {
  const color = type === 'office' ? '#3b82f6' : type === 'remote' ? '#22c55e' : type === 'hybrid' ? '#f59e0b' : '#6b7280'
  
  if (photoUrl) {
    return L.divIcon({
      className: 'custom-marker',
      html: `
        <div style="
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 3px solid ${color};
          background-image: url('${photoUrl}');
          background-size: cover;
          background-position: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        "></div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -40]
    })
  }
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background-color: ${color};
        border: 2px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  })
}

// Cluster marker icon showing count
const createClusterIcon = (count: number, dominantType: 'office' | 'remote' | 'hybrid' | null, isSelected: boolean = false) => {
  const color = dominantType === 'office' ? '#3b82f6' : dominantType === 'remote' ? '#22c55e' : dominantType === 'hybrid' ? '#f59e0b' : '#6b7280'
  
  // When selected, show a highlight ring
  const selectedStyles = isSelected ? `
    box-shadow: 0 0 0 4px rgba(147, 51, 234, 0.5), 0 2px 10px rgba(0,0,0,0.4);
    border: 3px solid #a855f7;
  ` : `
    box-shadow: 0 2px 10px rgba(0,0,0,0.4);
    border: 3px solid white;
  `
  
  return L.divIcon({
    className: 'custom-cluster-marker',
    html: `
      <div style="
        width: 44px;
        height: 44px;
        border-radius: 50%;
        background-color: ${color};
        ${selectedStyles}
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 600;
        font-size: 16px;
      ">${count}</div>
    `,
    iconSize: [44, 44],
    iconAnchor: [22, 44],
    popupAnchor: [0, -44]
  })
}

// Group members by proximity - threshold varies with zoom level
interface LocationCluster {
  key: string
  coordinates: { lat: number; lng: number }
  members: TeamMember[]
  dominantType: 'office' | 'remote' | 'hybrid' | null
}

const groupMembersByLocation = (members: TeamMember[], zoom: number): LocationCluster[] => {
  const clusters: LocationCluster[] = []
  
  // Dynamic threshold based on zoom level
  // At zoom 5 (country level): ~50km clustering
  // At zoom 10 (city level): ~1.5km clustering  
  // At zoom 15 (street level): ~50m clustering
  // At zoom 18+: ~10m clustering (almost no clustering)
  const baseThreshold = 0.5 // Base threshold in degrees at zoom 5
  const threshold = baseThreshold * Math.pow(2, 5 - zoom)
  
  members.forEach(member => {
    if (!member.location?.coordinates) return
    
    const { lat, lng } = member.location.coordinates
    
    // Find existing cluster within threshold
    const existingCluster = clusters.find(c => 
      Math.abs(c.coordinates.lat - lat) < threshold && 
      Math.abs(c.coordinates.lng - lng) < threshold
    )
    
    if (existingCluster) {
      existingCluster.members.push(member)
      // Update cluster center to average of all members
      const totalLat = existingCluster.members.reduce((sum, m) => sum + m.location!.coordinates!.lat, 0)
      const totalLng = existingCluster.members.reduce((sum, m) => sum + m.location!.coordinates!.lng, 0)
      existingCluster.coordinates = {
        lat: totalLat / existingCluster.members.length,
        lng: totalLng / existingCluster.members.length
      }
    } else {
      clusters.push({
        key: `${lat.toFixed(6)}-${lng.toFixed(6)}-${zoom}`,
        coordinates: { lat, lng },
        members: [member],
        dominantType: null
      })
    }
  })
  
  // Calculate dominant type for each cluster
  clusters.forEach(cluster => {
    const typeCounts = { office: 0, remote: 0, hybrid: 0, none: 0 }
    cluster.members.forEach(m => {
      const type = m.location?.type
      if (type === 'office') typeCounts.office++
      else if (type === 'remote') typeCounts.remote++
      else if (type === 'hybrid') typeCounts.hybrid++
      else typeCounts.none++
    })
    
    const maxCount = Math.max(typeCounts.office, typeCounts.remote, typeCounts.hybrid)
    if (maxCount === 0) {
      cluster.dominantType = null
    } else if (typeCounts.office === maxCount) {
      cluster.dominantType = 'office'
    } else if (typeCounts.remote === maxCount) {
      cluster.dominantType = 'remote'
    } else {
      cluster.dominantType = 'hybrid'
    }
    
    // Update key to be unique for this zoom level's clustering
    cluster.key = `cluster-${cluster.coordinates.lat.toFixed(6)}-${cluster.coordinates.lng.toFixed(6)}-z${zoom}`
  })
  
  return clusters
}

// Calculate spider positions around a center point - two tier layout
const calculateSpiderPositions = (
  center: { lat: number; lng: number },
  count: number,
  map: L.Map
): { lat: number; lng: number }[] => {
  const positions: { lat: number; lng: number }[] = []
  
  // Calculate radius based on zoom level
  const zoom = map.getZoom()
  // Base radius that works well at zoom 12, scales with zoom
  const baseRadius = 0.02
  const radius = baseRadius * Math.pow(2, 12 - zoom)
  
  const MAX_INNER_CIRCLE = 6
  
  if (count <= MAX_INNER_CIRCLE) {
    // Single tier - all in inner circle
    for (let i = 0; i < count; i++) {
      const angle = (2 * Math.PI * i) / count - Math.PI / 2 // Start from top
      positions.push({
        lat: center.lat + radius * Math.sin(angle),
        lng: center.lng + radius * Math.cos(angle) * 1.3
      })
    }
  } else {
    // Two tier layout
    const innerCount = MAX_INNER_CIRCLE
    const outerCount = count - MAX_INNER_CIRCLE
    const innerRadius = radius
    const outerRadius = radius * 1.8 // Outer circle is 1.8x the inner radius
    
    // Inner circle (first 6)
    for (let i = 0; i < innerCount; i++) {
      const angle = (2 * Math.PI * i) / innerCount - Math.PI / 2
      positions.push({
        lat: center.lat + innerRadius * Math.sin(angle),
        lng: center.lng + innerRadius * Math.cos(angle) * 1.3
      })
    }
    
    // Outer circle (remaining)
    for (let i = 0; i < outerCount; i++) {
      // Offset the outer circle slightly so markers don't align with inner
      const angle = (2 * Math.PI * i) / outerCount - Math.PI / 2 + (Math.PI / outerCount)
      positions.push({
        lat: center.lat + outerRadius * Math.sin(angle),
        lng: center.lng + outerRadius * Math.cos(angle) * 1.3
      })
    }
  }
  
  return positions
}

// Component to fit bounds on initial load only
const FitBounds: React.FC<{ members: TeamMember[] }> = ({ members }) => {
  const map = useMap()
  const [hasFitted, setHasFitted] = useState(false)
  
  useEffect(() => {
    if (hasFitted) return // Only fit once
    
    const membersWithCoords = members.filter(m => m.location?.coordinates)
    if (membersWithCoords.length > 0) {
      const bounds = L.latLngBounds(
        membersWithCoords.map(m => [m.location!.coordinates!.lat, m.location!.coordinates!.lng])
      )
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 })
      setHasFitted(true)
    }
  }, [members, map, hasFitted])
  
  return null
}

// Component to handle map events for collapsing clusters and tracking zoom
const MapEvents: React.FC<{ 
  onCollapse: () => void
  onZoomChange: (zoom: number) => void 
}> = ({ onCollapse, onZoomChange }) => {
  const map = useMap()
  
  useMapEvents({
    click: () => {
      onCollapse()
    },
    dragstart: () => {
      onCollapse()
    },
    zoomend: () => {
      onZoomChange(map.getZoom())
      onCollapse() // Collapse expanded cluster when zoom changes since clusters recalculate
    }
  })
  return null
}

// Component for expandable cluster with spider pattern
const ExpandableCluster: React.FC<{
  cluster: LocationCluster
  isExpanded: boolean
  onToggleExpand: () => void
  memberRolesMap: Map<string, RoleWithMember[]>
  getLocationIcon: (type: string | null | undefined) => React.ReactNode
}> = ({ cluster, isExpanded, onToggleExpand, memberRolesMap, getLocationIcon }) => {
  const map = useMap()
  
  // Calculate spider positions - recalculates when cluster changes (which happens on zoom)
  const spiderPositions = useMemo(() => {
    if (!isExpanded) return []
    return calculateSpiderPositions(cluster.coordinates, cluster.members.length, map)
  }, [isExpanded, cluster.coordinates, cluster.members.length, map])
  
  if (!isExpanded) {
    // Show cluster marker with count
    return (
      <Marker
        position={[cluster.coordinates.lat, cluster.coordinates.lng]}
        icon={createClusterIcon(cluster.members.length, cluster.dominantType)}
        zIndexOffset={0}
        eventHandlers={{
          click: (e) => {
            L.DomEvent.stopPropagation(e.originalEvent)
            onToggleExpand()
          }
        }}
      />
    )
  }
  
  // Expanded state - show cluster center + spider markers
  return (
    <>
      {/* Center marker (clickable to collapse) - lower z-index, highlighted */}
      <Marker
        position={[cluster.coordinates.lat, cluster.coordinates.lng]}
        icon={createClusterIcon(cluster.members.length, cluster.dominantType, true)}
        zIndexOffset={-1000}
        eventHandlers={{
          click: (e) => {
            L.DomEvent.stopPropagation(e.originalEvent)
            onToggleExpand()
          }
        }}
      />
      
      {/* Spider markers for each member - higher z-index */}
      {cluster.members.map((member, index) => {
        const position = spiderPositions[index]
        if (!position) return null
        
        const memberRoles = memberRolesMap.get(member.id) || []
        
        return (
          <Marker
            key={member.id}
            position={[position.lat, position.lng]}
            icon={createCustomIcon(member.location?.type ?? null, member.thumbnailPhotoUrl)}
            zIndexOffset={1000}
            eventHandlers={{
              click: (e) => {
                L.DomEvent.stopPropagation(e.originalEvent)
              }
            }}
          >
            <Popup>
              <div className="min-w-[200px]">
                <div className="flex items-center space-x-3 mb-2">
                  <img
                    src={member.thumbnailPhotoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name.fullName)}&background=8b5cf6&color=fff`}
                    alt={member.name.fullName}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-medium text-gray-900">{member.name.fullName}</p>
                    <p className="text-xs text-gray-500">{member.primaryEmail}</p>
                  </div>
                </div>
                {member.location?.label && (
                  <div className="flex items-center space-x-1 text-sm text-gray-600 mb-1">
                    {getLocationIcon(member.location.type)}
                    <span>{member.location.label}</span>
                  </div>
                )}
                {memberRoles.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {memberRoles.map(role => (
                      <span 
                        key={role.id}
                        className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full"
                      >
                        {role.title}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        )
      })}
    </>
  )
}

export const LocationView: React.FC<LocationViewProps> = ({
  teamMembers,
  roles,
  isAdmin,
  onUpdateLocation
}) => {
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null)
  const [editLocation, setEditLocation] = useState<{
    label: string
    type: 'office' | 'remote' | 'hybrid' | null
  }>({ label: '', type: null })
  const [saving, setSaving] = useState(false)
  const [viewMode, setViewMode] = useState<'map' | 'list'>('map')
  const [expandedClusterKey, setExpandedClusterKey] = useState<string | null>(null)
  const [mapZoom, setMapZoom] = useState(5) // Default zoom level
  const [showHiddenOnMap, setShowHiddenOnMap] = useState(false)

  const orgChartService = OrgChartService.getInstance()

  const hiddenCount = useMemo(
    () => teamMembers.filter(m => m.hidden).length,
    [teamMembers]
  )

  const visibleMembers = useMemo(() => {
    if (isAdmin && showHiddenOnMap) return teamMembers
    return teamMembers.filter(m => !m.hidden)
  }, [teamMembers, isAdmin, showHiddenOnMap])
  
  // Collapse expanded cluster
  const handleCollapseCluster = useCallback(() => {
    setExpandedClusterKey(null)
  }, [])
  
  // Handle zoom change
  const handleZoomChange = useCallback((zoom: number) => {
    setMapZoom(zoom)
  }, [])

  // Build member roles map
  const memberRolesMap = useMemo(() => {
    const map = new Map<string, RoleWithMember[]>()
    for (const role of roles) {
      if (role.assignedMemberId) {
        if (!map.has(role.assignedMemberId)) {
          map.set(role.assignedMemberId, [])
        }
        map.get(role.assignedMemberId)!.push(role)
      }
    }
    return map
  }, [roles])

  // Group members by location
  const membersByLocation = useMemo(() => {
    const groups = new Map<string, TeamMember[]>()
    const noLocation: TeamMember[] = []

    visibleMembers.forEach(member => {
      if (member.location?.label) {
        const key = member.location.label
        if (!groups.has(key)) {
          groups.set(key, [])
        }
        groups.get(key)!.push(member)
      } else {
        noLocation.push(member)
      }
    })

    return { groups, noLocation }
  }, [visibleMembers])

  // Location stats
  const locationStats = useMemo(() => {
    let office = 0, remote = 0, hybrid = 0, unknown = 0

    visibleMembers.forEach(member => {
      switch (member.location?.type) {
        case 'office': office++; break
        case 'remote': remote++; break
        case 'hybrid': hybrid++; break
        default: unknown++
      }
    })

    return { office, remote, hybrid, unknown, total: visibleMembers.length }
  }, [visibleMembers])

  // Group members by location for map clustering - recalculates when zoom changes
  const locationClusters = useMemo(() => {
    const membersWithCoords = visibleMembers.filter(m => m.location?.coordinates)
    return groupMembersByLocation(membersWithCoords, mapZoom)
  }, [visibleMembers, mapZoom])

  const handleCancelEdit = () => {
    setEditingMemberId(null)
    setEditLocation({ label: '', type: null })
  }

  const handleSaveLocation = async (memberId: string) => {
    try {
      setSaving(true)

      // Find coordinates if it's a known office location
      const officeMatch = OFFICE_LOCATIONS.find(
        o => o.label.toLowerCase() === editLocation.label.toLowerCase()
      )

      await orgChartService.updateTeamMember(memberId, {
        location: {
          label: editLocation.label || null,
          type: editLocation.type,
          coordinates: officeMatch?.coordinates || null
        }
      })

      setEditingMemberId(null)
      setEditLocation({ label: '', type: null })
      onUpdateLocation()
    } catch (error) {
      console.error('Error saving location:', error)
    } finally {
      setSaving(false)
    }
  }

  const getLocationIcon = (type: string | null | undefined) => {
    switch (type) {
      case 'office': return <Building2 className="w-4 h-4" />
      case 'remote': return <Home className="w-4 h-4" />
      case 'hybrid': return <Briefcase className="w-4 h-4" />
      default: return <MapPin className="w-4 h-4" />
    }
  }

  const getLocationColor = (type: string | null | undefined) => {
    switch (type) {
      case 'office': return 'text-blue-600 bg-blue-100'
      case 'remote': return 'text-green-600 bg-green-100'
      case 'hybrid': return 'text-amber-600 bg-amber-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  return (
    <div>
      {/* Stats Summary - Compact */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        <div className="bg-blue-50 rounded-lg px-3 py-1.5 flex items-center justify-center space-x-2">
          <Building2 className="w-4 h-4 text-blue-600" />
          <span className="text-lg font-bold text-blue-700">{locationStats.office}</span>
          <span className="text-xs text-blue-600">Office</span>
        </div>
        <div className="bg-green-50 rounded-lg px-3 py-1.5 flex items-center justify-center space-x-2">
          <Home className="w-4 h-4 text-green-600" />
          <span className="text-lg font-bold text-green-700">{locationStats.remote}</span>
          <span className="text-xs text-green-600">Remote</span>
        </div>
        <div className="bg-amber-50 rounded-lg px-3 py-1.5 flex items-center justify-center space-x-2">
          <Briefcase className="w-4 h-4 text-amber-600" />
          <span className="text-lg font-bold text-amber-700">{locationStats.hybrid}</span>
          <span className="text-xs text-amber-600">Hybrid</span>
        </div>
        <div className="bg-gray-50 rounded-lg px-3 py-1.5 flex items-center justify-center space-x-2">
          <AlertCircle className="w-4 h-4 text-gray-500" />
          <span className="text-lg font-bold text-gray-600">{locationStats.unknown}</span>
          <span className="text-xs text-gray-500">Not Set</span>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-1">
          <button
            onClick={() => setViewMode('map')}
            className={`flex items-center space-x-1 px-2 py-1 text-sm rounded transition-colors ${
              viewMode === 'map'
                ? 'bg-purple-100 text-purple-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <MapIcon className="w-3 h-3" />
            <span>Map</span>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center space-x-1 px-2 py-1 text-sm rounded transition-colors ${
              viewMode === 'list'
                ? 'bg-purple-100 text-purple-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <List className="w-3 h-3" />
            <span>List</span>
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2 justify-end">
          {isAdmin && hiddenCount > 0 && (
            <label className="flex items-center gap-1.5 text-xs text-amber-900 cursor-pointer">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                checked={showHiddenOnMap}
                onChange={(e) => setShowHiddenOnMap(e.target.checked)}
              />
              <span>Show hidden ({hiddenCount})</span>
            </label>
          )}
          <p className="text-xs text-gray-500">
            {visibleMembers.filter(m => m.location?.coordinates).length}/{visibleMembers.length} with coordinates
          </p>
        </div>
      </div>

      {/* Map View */}
      {viewMode === 'map' && (
        <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm" style={{ height: 'calc(100vh - 370px)', minHeight: '300px' }}>
          <MapContainer
            center={[-41.0, 174.0]} // Center on New Zealand
            zoom={5}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <FitBounds members={visibleMembers.filter(m => m.location?.coordinates)} />
            <MapEvents onCollapse={handleCollapseCluster} onZoomChange={handleZoomChange} />
            
            {/* Render clustered markers */}
            {locationClusters.map(cluster => {
              const isSingleMember = cluster.members.length === 1
              const member = cluster.members[0]
              
              if (isSingleMember) {
                // Single member - show individual marker with photo
                const memberRoles = memberRolesMap.get(member.id) || []
                return (
                  <Marker
                    key={cluster.key}
                    position={[cluster.coordinates.lat, cluster.coordinates.lng]}
                    icon={createCustomIcon(member.location?.type ?? null, member.thumbnailPhotoUrl)}
                  >
                    <Popup>
                      <div className="min-w-[200px]">
                        <div className="flex items-center space-x-3 mb-2">
                          <img
                            src={member.thumbnailPhotoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name.fullName)}&background=8b5cf6&color=fff`}
                            alt={member.name.fullName}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div>
                            <p className="font-medium text-gray-900">{member.name.fullName}</p>
                            <p className="text-xs text-gray-500">{member.primaryEmail}</p>
                          </div>
                        </div>
                        {member.location?.label && (
                          <div className="flex items-center space-x-1 text-sm text-gray-600 mb-1">
                            {getLocationIcon(member.location.type)}
                            <span>{member.location.label}</span>
                          </div>
                        )}
                        {memberRoles.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {memberRoles.map(role => (
                              <span 
                                key={role.id}
                                className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full"
                              >
                                {role.title}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                )
              }
              
              // Multiple members - show expandable cluster
              return (
                <ExpandableCluster
                  key={cluster.key}
                  cluster={cluster}
                  isExpanded={expandedClusterKey === cluster.key}
                  onToggleExpand={() => {
                    setExpandedClusterKey(
                      expandedClusterKey === cluster.key ? null : cluster.key
                    )
                  }}
                  memberRolesMap={memberRolesMap}
                  getLocationIcon={getLocationIcon}
                />
              )
            })}
          </MapContainer>
        </div>
      )}

      {/* List View - Locations grouped */}
      {viewMode === 'list' && (
      <div className="space-y-6">
        {/* Known locations */}
        {Array.from(membersByLocation.groups.entries()).map(([locationLabel, members]) => {
          const locationType = members[0]?.location?.type
          
          return (
            <div key={locationLabel} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className={`flex items-center justify-between px-4 py-3 ${getLocationColor(locationType)}`}>
                <div className="flex items-center space-x-2">
                  {getLocationIcon(locationType)}
                  <h3 className="font-medium">{locationLabel}</h3>
                  <span className="text-sm opacity-75">({members.length})</span>
                </div>
              </div>
              
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {members.map(member => (
                  <div key={member.id} className="relative">
                    {editingMemberId === member.id ? (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Location Name
                            </label>
                            <input
                              type="text"
                              value={editLocation.label}
                              onChange={(e) => setEditLocation({ ...editLocation, label: e.target.value })}
                              placeholder="e.g., Auckland Office, Remote - Wellington"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                              list="location-suggestions"
                            />
                            <datalist id="location-suggestions">
                              {OFFICE_LOCATIONS.map(loc => (
                                <option key={loc.label} value={loc.label} />
                              ))}
                            </datalist>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Type
                            </label>
                            <div className="flex space-x-2">
                              {(['office', 'remote', 'hybrid'] as const).map(type => (
                                <button
                                  key={type}
                                  onClick={() => setEditLocation({ ...editLocation, type })}
                                  className={`flex-1 px-2 py-1 text-xs rounded capitalize ${
                                    editLocation.type === type
                                      ? getLocationColor(type)
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                >
                                  {type}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={handleCancelEdit}
                              className="p-1.5 text-gray-500 hover:text-gray-700 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleSaveLocation(member.id)}
                              disabled={saving}
                              className="p-1.5 text-green-600 hover:text-green-700 rounded disabled:opacity-50"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <PersonCard 
                          member={member} 
                          roles={memberRolesMap.get(member.id)}
                          compact 
                          showExcludedFromDirectoryBadge={isAdmin && showHiddenOnMap && member.hidden === true}
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        })}

        {/* No location set */}
        {membersByLocation.noLocation.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-100 text-gray-600">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-4 h-4" />
                <h3 className="font-medium">Location Not Set</h3>
                <span className="text-sm opacity-75">({membersByLocation.noLocation.length})</span>
              </div>
            </div>
            
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {membersByLocation.noLocation.map(member => (
                <div key={member.id} className="relative">
                  {editingMemberId === member.id ? (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Location Name
                          </label>
                          <input
                            type="text"
                            value={editLocation.label}
                            onChange={(e) => setEditLocation({ ...editLocation, label: e.target.value })}
                            placeholder="e.g., Auckland Office, Remote - Wellington"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                            list="location-suggestions-2"
                          />
                          <datalist id="location-suggestions-2">
                            {OFFICE_LOCATIONS.map(loc => (
                              <option key={loc.label} value={loc.label} />
                            ))}
                          </datalist>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Type
                          </label>
                          <div className="flex space-x-2">
                            {(['office', 'remote', 'hybrid'] as const).map(type => (
                              <button
                                key={type}
                                onClick={() => setEditLocation({ ...editLocation, type })}
                                className={`flex-1 px-2 py-1 text-xs rounded capitalize ${
                                  editLocation.type === type
                                    ? getLocationColor(type)
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                {type}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={handleCancelEdit}
                            className="p-1.5 text-gray-500 hover:text-gray-700 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleSaveLocation(member.id)}
                            disabled={saving}
                            className="p-1.5 text-green-600 hover:text-green-700 rounded disabled:opacity-50"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <PersonCard 
                        member={member} 
                        roles={memberRolesMap.get(member.id)}
                        compact 
                        showExcludedFromDirectoryBadge={isAdmin && showHiddenOnMap && member.hidden === true}
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {visibleMembers.length === 0 && (
          <div className="text-center py-12">
            <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No team members to show on the map</p>
            {isAdmin && teamMembers.length > 0 && hiddenCount > 0 && !showHiddenOnMap && (
              <p className="text-sm text-amber-700 mt-2">
                Hidden members are omitted here. Enable &quot;Show hidden&quot; to include them.
              </p>
            )}
            {teamMembers.length === 0 && (
              <p className="text-sm text-gray-400 mt-1">
                Sync from Google Directory to load team members
              </p>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  )
}
